import { Application } from '@frontend/functionality/types';
import { TransactionClient } from '@frontend/transaction/api';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { type Dispatch, type SetStateAction, useState } from 'react';

import { useScreen } from '../useScreen';

export interface TransactionSchedulingViewProps {
    application: Application | null;
    expectedDropOff: Date | undefined;
    setExpectedDropOff: Dispatch<SetStateAction<Date | undefined>>;
    expectedPickUp: Date | undefined;
    setExpectedPickUp: Dispatch<SetStateAction<Date | undefined>>;
    invalidRange: boolean;
    submitDisabled: boolean;
    onSubmit: () => Promise<void>;
}

const parseDate = (value: string | null | undefined): Date | undefined => {
    if (!value) return undefined;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const useTransactionScheduling = (props: { wizard_id: string }): TransactionSchedulingViewProps => {
    const { store, updateWizardData, onSubmit: onContinue } = useScreen(props.wizard_id);
    const transaction = store?.data.transaction;
    const [expectedDropOff, setExpectedDropOff] = useState<Date | undefined>(() => parseDate(transaction?.schedule_start_timestamp));
    const [expectedPickUp, setExpectedPickUp] = useState<Date | undefined>(() => parseDate(transaction?.schedule_stop_timestamp));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const invalidRange = !!expectedDropOff && !!expectedPickUp && expectedPickUp.getTime() < expectedDropOff.getTime();

    const onSubmit = async () => {
        if (!store || !transaction || invalidRange || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const updatedTransaction = await TransactionClient.patchTransaction(transaction.account_id, transaction.id, {
                schedule_start_timestamp: expectedDropOff?.toISOString() ?? null,
                schedule_stop_timestamp: expectedPickUp?.toISOString() ?? null
            });

            updateWizardData({
                transaction: updatedTransaction
            });

            const nextStep = store.steps.find((step) => step.screen === ScreenType.TRANSACTION_SCHEDULING && step.trigger === ScreenTrigger.SUBMIT);
            await onContinue(nextStep);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        application: store?.functionality.application ?? transaction?.application ?? null,
        expectedDropOff,
        setExpectedDropOff,
        expectedPickUp,
        setExpectedPickUp,
        invalidRange,
        submitDisabled: !transaction || invalidRange || isSubmitting,
        onSubmit
    };
};
